<template>
  <b-modal
    id="vessel-rotation-modal"
    v-model="visible"
    :title="vessel ? 'Rotasjonsregel – ' + vessel.name : 'Rotasjonsregel'"
    size="lg"
    no-close-on-backdrop
  >
    <template v-if="vessel">
      <b-form-group label="Hvordan roterer skiftene?" label-class="font-weight-bold">
        <b-form-radio-group v-model="form.mode" stacked>
          <b-form-radio v-for="m in modes" :key="m.value" :value="m.value" class="mb-2">
            <span class="font-weight-bold">{{ m.text }}</span>
            <small class="d-block text-muted">{{ m.help }}</small>
          </b-form-radio>
        </b-form-radio-group>
      </b-form-group>

      <b-form-group label-class="font-weight-bold">
        <template #label>
          Skift i rotasjonen
          <small class="text-muted font-weight-normal">(rekkefølgen er rotasjonsrekkefølgen)</small>
        </template>
        <div
          v-for="(s, i) in orderedSkifts"
          :key="s.id"
          class="cp-rot-item border rounded p-2 mb-2 d-flex align-items-center"
          :class="{ 'cp-rot-off': !form.skifts.includes(s.id) }"
        >
          <b-form-checkbox
            :checked="form.skifts.includes(s.id)"
            class="mr-2"
            @change="(on) => toggle(s.id, on)"
          />
          <span class="cp-rot-swatch mr-2" :style="{ background: s.color }"></span>
          <span class="flex-grow-1">
            {{ s.name }}
            <small class="text-muted">· {{ memberCount(s.id) }} personer</small>
          </span>
          <template v-if="form.skifts.includes(s.id)">
            <b-badge v-if="form.skifts.indexOf(s.id) === 0" pill variant="primary" class="mr-2">Starter</b-badge>
            <b-button size="sm" variant="link" class="p-0 mr-2" :disabled="i === 0" @click="move(s.id, -1)">
              <i class="ion ion-ios-arrow-up"></i>
            </b-button>
            <b-button size="sm" variant="link" class="p-0" :disabled="form.skifts.indexOf(s.id) === form.skifts.length - 1" @click="move(s.id, 1)">
              <i class="ion ion-ios-arrow-down"></i>
            </b-button>
          </template>
        </div>
        <p v-if="!orderedSkifts.length" class="text-muted small">
          Fartøyet har ingen skift ennå. Opprett skift under
          <b-link to="/dashboard/skift">Skift</b-link>.
        </p>
      </b-form-group>

      <b-form-row v-if="form.mode === 'periode'">
        <b-col sm="4">
          <b-form-group label="Periodelengde (dager)">
            <b-form-input v-model.number="form.period_days" type="number" min="1" max="365" />
          </b-form-group>
        </b-col>
        <b-col sm="4">
          <b-form-group label="Startdato">
            <b-form-input v-model="form.anchor_date" type="date" />
          </b-form-group>
        </b-col>
        <b-col sm="4">
          <b-form-group label="Byttetidspunkt">
            <b-form-input v-model="form.changeover_time" type="time" />
          </b-form-group>
        </b-col>
      </b-form-row>
      <p v-if="form.mode === 'periode' && form.anchor_date" class="small text-muted mt-n2">
        Bytte hver {{ weekdayName }}<span v-if="form.changeover_time"> kl. {{ form.changeover_time }}</span>.
        Første skift i listen starter {{ fmt(form.anchor_date) }}.
      </p>

      <!-- Preview -->
      <template v-if="form.mode === 'periode' && preview.length">
        <h6 class="font-weight-bold mt-3">Neste perioder</h6>
        <div class="d-flex flex-wrap">
          <div
            v-for="p in preview"
            :key="p.from"
            class="cp-rot-period border rounded p-2 mr-2 mb-2 small"
            :style="{ borderLeft: '4px solid ' + p.color }"
          >
            <div class="font-weight-bold">{{ p.name }}</div>
            <div class="text-muted">{{ fmt(p.from) }} – {{ fmt(p.to) }}</div>
          </div>
        </div>
      </template>

      <b-alert show variant="info" class="small mt-3 mb-0">
        Når du lagrer, oppdateres skift og mannskap på alle kommende ordrer for {{ vessel.name }} som ikke er låst.
        Avsluttede og passerte ordrer endres ikke.
      </b-alert>
      <b-alert v-if="error" show variant="danger" class="small mt-2 mb-0">{{ error }}</b-alert>
    </template>

    <template #modal-footer>
      <div class="w-100">
        <b-button v-if="ruleId" variant="outline-danger" class="float-left" :disabled="saving" @click="remove()">Fjern regel</b-button>
        <b-button variant="primary" class="float-right" :disabled="saving || !canSave" @click="save()">
          <b-spinner v-if="saving" small class="mr-1"></b-spinner>Lagre
        </b-button>
        <b-button variant="default" class="float-right mr-2" @click="visible = false">Avbryt</b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import dayjs from "dayjs";
import api, { MODES, skiftColor, errorText } from "./api";

export default {
  name: "VesselRotationModal",
  props: {
    plan: { type: Object, default: null },
  },
  data() {
    return {
      visible: false,
      vessel: null,
      ruleId: null,
      form: { mode: "periode", skifts: [], period_days: 14, anchor_date: null, changeover_time: "" },
      modes: MODES,
      saving: false,
      error: null,
    };
  },
  computed: {
    skifts() {
      return ((this.plan && this.plan.skifts) || []).map((s, i) => ({ ...s, color: skiftColor(s, i) }));
    },
    // Skifts in the rule first (in rule order), then the vessel's other skifts.
    orderedSkifts() {
      if (!this.vessel) return [];
      const inRule = this.form.skifts.map((id) => this.skifts.find((s) => s.id === id)).filter(Boolean);
      const rest = this.skifts.filter((s) => s.vessel === this.vessel.id && !this.form.skifts.includes(s.id));
      return inRule.concat(rest);
    },
    canSave() {
      if (this.form.mode === "manuell") return true;
      if (!this.form.skifts.length) return false;
      if (this.form.mode === "periode") return !!this.form.anchor_date && this.form.period_days >= 1;
      return true;
    },
    weekdayName() {
      return dayjs(this.form.anchor_date).format("dddd");
    },
    // Display only: the API decides the real plan.
    preview() {
      const p = parseInt(this.form.period_days, 10);
      if (!this.form.anchor_date || !(p >= 1) || !this.form.skifts.length) return [];
      const anchor = dayjs(this.form.anchor_date);
      const k0 = Math.max(0, Math.floor(dayjs().startOf("day").diff(anchor, "day") / p));
      const out = [];
      for (let k = k0; k < k0 + 6; k++) {
        const id = this.form.skifts[k % this.form.skifts.length];
        const s = this.skifts.find((x) => x.id === id) || {};
        out.push({
          name: s.name,
          color: s.color,
          from: anchor.add(k * p, "day").format("YYYY-MM-DD"),
          to: anchor.add(k * p + p - 1, "day").format("YYYY-MM-DD"),
        });
      }
      return out;
    },
  },
  methods: {
    open(vessel) {
      this.vessel = vessel;
      this.error = null;
      const rule = vessel.rule;
      this.ruleId = rule ? rule.id : null;
      const vesselSkifts = this.skifts.filter((s) => s.vessel === vessel.id).map((s) => s.id);
      this.form = rule
        ? {
            mode: rule.mode,
            skifts: rule.skifts.slice(),
            period_days: rule.period_days || 14,
            anchor_date: rule.anchor_date,
            changeover_time: rule.changeover_time || "",
          }
        : {
            mode: "periode",
            skifts: vesselSkifts.slice(0, 2),
            period_days: 14,
            anchor_date: dayjs().startOf("isoWeek").format("YYYY-MM-DD"),
            changeover_time: "",
          };
      this.visible = true;
    },
    memberCount(skiftId) {
      return ((this.plan && this.plan.crew) || []).filter((c) => c.skift === skiftId).length;
    },
    toggle(id, on) {
      if (on && !this.form.skifts.includes(id)) this.form.skifts.push(id);
      if (!on) this.form.skifts = this.form.skifts.filter((x) => x !== id);
    },
    move(id, dir) {
      const list = this.form.skifts.slice();
      const i = list.indexOf(id);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= list.length) return;
      [list[i], list[j]] = [list[j], list[i]];
      this.form.skifts = list;
    },
    fmt(date) {
      return date ? dayjs(date).format("DD.MM.YYYY") : "";
    },
    async save() {
      this.saving = true;
      this.error = null;
      try {
        await api.saveRule({
          id: this.ruleId || undefined,
          vessel: this.vessel.id,
          mode: this.form.mode,
          skifts: this.form.skifts,
          period_days: parseInt(this.form.period_days, 10) || 14,
          anchor_date: this.form.anchor_date || null,
          changeover_time: this.form.changeover_time || null,
          active: true,
        });
        this.visible = false;
        this.$bvToast.toast("Rotasjonsregelen er lagret og ordrene er oppdatert.", {
          title: "Lagret",
          variant: "success",
          solid: true,
          autoHideDelay: 2500,
        });
        this.$emit("saved");
      } catch (error) {
        console.log(error);
        this.error = errorText(error);
      } finally {
        this.saving = false;
      }
    },
    async remove() {
      const ok = await this.$bvModal.msgBoxConfirm(
        "Fjerne rotasjonsregelen? Ordrene beholder mannskapet de har nå, men oppdateres ikke lenger automatisk.",
        { title: "Fjern regel", okTitle: "Fjern regel", cancelTitle: "Avbryt", okVariant: "danger" }
      );
      if (!ok) return;
      this.saving = true;
      try {
        await api.deleteRule(this.ruleId);
        this.visible = false;
        this.$emit("saved");
      } catch (error) {
        this.error = errorText(error);
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style lang="css">
.cp-rot-item {
  background: #f8f9fa;
}
.cp-rot-off {
  opacity: 0.6;
}
.cp-rot-swatch {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 3px;
}
.cp-rot-period {
  min-width: 150px;
}
</style>
